/** Known pipeline steps (subset may appear depending on scenario). */
export const SETUP_STEP_KEYS = [
  "SEARCHING_INDUSTRY_NEWS",
  "CREATING_INDUSTRY_OPPORTUNITIES",
  "LOOKING_FOR_GLOBAL_SEARCH_TRENDS",
  "CREATING_GLOBAL_OPPORTUNITIES",
];

export const SETUP_STEP_LABELS = {
  SEARCHING_INDUSTRY_NEWS: "Searching industry news…",
  CREATING_INDUSTRY_OPPORTUNITIES: "Creating industry opportunities…",
  LOOKING_FOR_GLOBAL_SEARCH_TRENDS: "Looking for global search trends…",
  CREATING_GLOBAL_OPPORTUNITIES: "Creating global opportunities…",
};

export const TERMINAL_SETUP_STATUSES = new Set([
  "completed",
  "completed_with_errors",
  "failed",
]);

export function humanizeSetupStep(stepKey) {
  if (!stepKey) return "";
  if (SETUP_STEP_LABELS[stepKey]) return SETUP_STEP_LABELS[stepKey];
  return String(stepKey)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

export function isProgressStepKey(stepKey) {
  return Boolean(stepKey);
}

export function isTerminalSetupStatus(status) {
  return TERMINAL_SETUP_STATUSES.has(status);
}

export function isSuccessfulSetupStatus(status) {
  return status === "completed" || status === "completed_with_errors";
}

export function extractOpportunityCountFromSetup(state = {}) {
  const numeric = Number(state.total_opportunities_generated);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

/**
 * Map GET /profile/business-profile/setup state to the visible step list.
 * Steps appear dynamically; multiple steps may be `active` in parallel.
 */
export function mapSetupStateToSteps(state = {}) {
  const byKey = new Map();
  let nextOrder = 0;

  const touchStep = (key) => {
    if (!isProgressStepKey(key)) return null;

    if (byKey.has(key)) return byKey.get(key);

    const step = {
      key,
      label: humanizeSetupStep(key),
      status: "pending",
      order: nextOrder,
    };
    nextOrder += 1;
    byKey.set(key, step);
    return step;
  };

  const events = Array.isArray(state.events) ? state.events : [];

  for (const event of events) {
    const key = event?.event;
    if (!isProgressStepKey(key)) continue;

    const step = touchStep(key);
    if (!step) continue;

    if (event.action === "completed") {
      step.status = "done";
    } else if (event.action === "started" && step.status !== "done") {
      step.status = "active";
    }
  }

  if (state.status === "running" && isProgressStepKey(state.current_event)) {
    const step = touchStep(state.current_event);
    if (step && step.status !== "done") {
      step.status = "active";
    }
  }

  if (isSuccessfulSetupStatus(state.status)) {
    for (const step of byKey.values()) {
      step.status = "done";
    }
  }

  return Array.from(byKey.values()).sort((a, b) => a.order - b.order);
}

export function finalizeSetupSteps(steps) {
  return steps.map((step) => ({ ...step, status: "done" }));
}
